import { TextField, Tooltip } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React, { FC, useState, useEffect, ReactNode } from "react";
import { IDataRequest, IDataResponse, IHttpClient, STATUS_CODE } from "src/framework/providers/api";

type InputComboBoxProperties = {
    id?: string;
    value?: any;
    provider: IHttpClient;
    path: string;
    label?: string;
    textField: string;
    valueField: string;
    autoComplete?: boolean;
    disabled?: boolean;
    helperText?: ReactNode;
    prefix?: ReactNode;
    renderOptions?: (option: any) => string;
    renderOptionsLabel?: (option: any) => string;
    onChange: (value: any) => void;
    onBlur?: Function;
    onFocus?: Function;
}

const InputComboBox: FC<InputComboBoxProperties> = ({
    id,
    label,
    value,
    provider,
    path,
    textField,
    disabled = false,
    autoComplete = false,
    valueField,
    renderOptions,
    renderOptionsLabel,
    onChange,
    helperText,
    prefix,
    onBlur,
    onFocus,
}) => {
    const pathPrevRef = React.useRef("");

    const [options, setOptions] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [localValue, setLocalValue] = React.useState<any>();
    const [inputValue, setInputValue] = React.useState('');

    const getOptions = async (): Promise<IDataResponse> => {
        setLoading(true)
        const request: IDataRequest = {
            url: path
        }

        return await provider.get(request);
    }

    const loadData = () => {
        if (pathPrevRef.current === path) {
            return;
        }
        pathPrevRef.current = path;

        setLoading(true);
        getOptions()
            .then((response) => {
                if (response.statusCode === STATUS_CODE.OK) {
                    setOptions(response?.data?.rows || response?.data || [])
                    return;
                } else {
                    setOptions([]);
                }
            })
            .catch((error) => { })
            .finally(() => {
                setLoading(true);
            });
    }

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    useEffect(() => {
        loadData();
    }, [path]);

    return <>
        <Tooltip title={localValue && renderOptions ? renderOptions(localValue) : ""} >
            <Autocomplete
                fullWidth
                id={id}
                value={value}
                disabled={disabled}
                options={options}
                autoComplete={autoComplete}
                getOptionLabel={(option) => {
                    return renderOptionsLabel ? renderOptionsLabel(option) : option[textField]
                }}
                getOptionSelected={(option, value) => option[valueField] === value[valueField]}
                onChange={(event, value, reason, details) => {
                    onChange(value)
                }}
                renderOption={(option) => {
                    return renderOptions ? renderOptions(option) : option[textField]
                }}
                renderInput={(params) => <TextField
                        {...params}
                        helperText={helperText}
                        InputLabelProps={{
                            shrink: true
                        }}
                        label={label}
                        variant="outlined"
                        size="small"
                        margin="normal"
                        fullWidth
                        value={inputValue}
                    />
                }
                onFocus={(event: any) => {
                    if (onFocus) {
                        onFocus(event)
                    }
                }}
                onBlur={(event: any) => {
                    if (onBlur) {
                        onBlur(event)
                    }
                }}
            />
        </Tooltip>
    </>
}

export { InputComboBox }